import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Form, Image, Dropdown } from 'react-bootstrap';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { type SearchResultType } from '@api/search';
import type { SelectedSupplier } from "./AddSupplier";
import { getRandomIntMaxTenk } from "@helper/numberHelper";

import scss from './SupplierSearch.module.scss';
import { faClose, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { useDebounce } from "@hooks/useDebounce";
import Loading from '@ui/Loading';
import useSupplierSearchQuery from "@queries/useSupplierSearchQuery";
import { getCDNImageUrl } from "@helper/image";

const SupplierSearchResult = ({ suppliers }: { suppliers: SearchResultType[] }) => (<>
    {suppliers?.map(item => {
        return (<Dropdown.Item key={item.Id} eventKey={item.Id} data-testid="searched-supplier" className={scss.searchResult}>
            <Image src={getCDNImageUrl(item.ImageUrl, { width: 44, height: 44})} className={scss.searchResultImage} />
            <div className={scss.searchResultTitle}>
                <div data-supplierid={item.Id}>{item.Name}</div>
                <div>
                    {(!!item.SupplierCity && !!item.SupplierState) ?
                    `${item.SupplierCity}, ${item.SupplierState}` : 
                    (!!item.SupplierCity ? item.SupplierCity : item.SupplierState)}
                </div>
            </div>
        </Dropdown.Item>)
    })}
</>);

type SearchInputProps = {
    search: string,
    categoryName: string,
    isFocus: boolean,
    placeholder?: string,
    label?: string,
    disabled: boolean,
    formElementHeight?: boolean,
    handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void,
    onClick: () => void,
}

const SearchInput = React.forwardRef(({ search, categoryName, isFocus, placeholder, label, disabled, formElementHeight, handleChange, onClick }: SearchInputProps, _ref) => {
    const controlName = `supplierSearch-${getRandomIntMaxTenk()}`;
    const ref = React.useRef<HTMLInputElement>(null);
    useEffect(() => {
        if (isFocus) {
            ref?.current?.focus();
        }
    }, []);
    return <div className={`w-100${!!label ? ' form-floating' : ''}`}>
                <Form.Control
                    disabled={disabled}
                    ref={ref}
                    name={controlName}
                    data-testid="search-input"
                    placeholder={!!placeholder ? placeholder : `Name of the ${categoryName} you've booked`}
                    onChange={handleChange}
                    value={search}
                    className={!!formElementHeight ? scss.searchFormText : scss.searchText}
                    onClick={onClick}
                />
                {!!label && <Form.Label htmlFor={controlName}>{label}</Form.Label>}
            </div>
})

export const useSearchSuppliers = function (searchTerm: string, categorySlug: string): {
    isLoading: boolean;
    suppliers: SearchResultType[] | undefined;
    searchId: string;
} {
    const { isLoading, data: suppliers } = useSupplierSearchQuery(searchTerm, categorySlug, true);
    
    const searchId = useMemo(() => suppliers?.length
        ? suppliers.map(({ Id }) => Id).join(',')
        : (new Date()).getTime().toString()
    , [suppliers]);
    return { isLoading, suppliers, searchId };
}

const mapSearchResultToSelectedSupplier = (searchResult: SearchResultType): SelectedSupplier => ({
    Id: searchResult.Id.toString(),
    Name: searchResult.Name,
    Thumbnail: searchResult.Thumbnail,
    Url: searchResult.Url,
    CategorySlug: searchResult.CategorySlug,
    RegionSlug: searchResult.RegionSlug,
    Rating: searchResult.AverageRating,
    ReviewCount: searchResult.ReviewCount,
    City: searchResult.SupplierCity,
    State: searchResult.SupplierState,
})

type SupplierSearchProps = {
    categoryName: string,
    categorySlug: string,
    placeholder?: string,
    label?: string,
    hideBackground?: boolean,
    isFocus?: boolean,
    disabled?: boolean,
    hideAfterSelect?: boolean,
    callOnClear?: boolean,
    defaultValue?: string,
    formElementHeight?: boolean,
    defaultSearch?: string,
    noCustomSupplier?: boolean,
    handleSaveSupplier: (supplier: SelectedSupplier | null) => void | undefined,
}

function SupplierSearch({
    categoryName,
    categorySlug,
    placeholder,
    label,
    hideBackground,
    isFocus = true,
    disabled = false,
    hideAfterSelect = false,
    callOnClear = false,
    defaultValue = '',
    formElementHeight = false,
    defaultSearch = '',
    noCustomSupplier,
    handleSaveSupplier
}: SupplierSearchProps) {
    const [search, setSearch] = useState<string>(defaultSearch);
    const [showDropdown, setShowDropdown] = useState<boolean>(false);

    const deferredSearchTerm = useDebounce(search, 400);
    const { isLoading, suppliers, searchId } = useSearchSuppliers(deferredSearchTerm, categorySlug);

    const clearSearch = () => {
        setSearch('');
        setShowDropdown(false);
        (callOnClear && !!handleSaveSupplier) && handleSaveSupplier(null);
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        event.preventDefault();
        setSearch(event.target.value);
        !showDropdown && setShowDropdown(true);
    };

    const handleSelectedSupplier = (supplier: SelectedSupplier) => {
        setSearch(supplier.Name);

        if (!!handleSaveSupplier) {
            handleSaveSupplier(supplier);
            hideAfterSelect && setShowDropdown(false);
        }
        else {
            (window as any).setSelectedSupplier(supplier);
            setShowDropdown(false);
        }
    };

    const handleDopdownSupplierSelect = useCallback((supplierId: string | null) => {
        // 'custom:' hack used because name of supplier can be look like a number so we can't check is it number or not
        if (supplierId?.startsWith('custom:')) {
            handleSelectedSupplier({ Id: (getRandomIntMaxTenk() * -1).toString(), Name: supplierId.replace(/^custom\:/, ''), Thumbnail: null, Url: null, CategorySlug: categorySlug, RegionSlug: null });
        } else {
            const selectedSupplier = suppliers?.find(s => s.Id === Number(supplierId));
            if (selectedSupplier != null) {
                handleSelectedSupplier(mapSearchResultToSelectedSupplier(selectedSupplier));
            }
        }
    }, [searchId])

    useEffect(() => {
        (window as any)?.supplierSearchInit?.();

        if (!!defaultValue) {
            setSearch(defaultValue);
            setShowDropdown(false);
        }
    }, [defaultValue]);

    return (<div className="flex-grow-1" data-testid="suppliersearch">
        <Dropdown
            show={showDropdown}
            autoClose="inside"
            onSelect={handleDopdownSupplierSelect}
            className={scss.supplierSearch}
        >
            <div className={`${scss.inputContainer} ${!hideBackground ? scss.inputContainerWithBackground : ''}`}>
                <Dropdown.Toggle
                    as={SearchInput}
                    search={search}
                    handleChange={handleChange}
                    categoryName={categoryName}
                    isFocus={!!isFocus}
                    placeholder={placeholder}
                    label={label}
                    disabled={disabled}
                    formElementHeight={formElementHeight}
                    onClick={() => setShowDropdown(true)}
                />
                <div className={scss.searchAction}>
                    {isLoading ?
                        <Loading /> :
                        <Icon icon={(!!search ? faClose : faMagnifyingGlass)} style={{width: '1rem', height: '1rem'}} onClick={clearSearch} />}
                </div>
            </div>

            {showDropdown && <Dropdown.Menu className={`w-100 ${scss.searchDropdown}`}>
                {(deferredSearchTerm !== '' && suppliers != null) && <SupplierSearchResult suppliers={suppliers} />}
                {(search.length > 2 && !noCustomSupplier)? <>
                {suppliers?.length ? <div className={scss.divider}/> : null}
                <Dropdown.Item eventKey={`custom:${search}`} className={`${scss.searchResult} ${scss.customSupplier}`} data-testid="custom-supplier">
                    <div>Can't find them?</div> <span>+ Add custom supplier</span>
                </Dropdown.Item></> : <></>}
            </Dropdown.Menu>}
        </Dropdown>
    </div>)
}

export default SupplierSearch;